import { VisionWindowManager } from '../../window/manager';
import { PM_Type, PreventativeMaintenanceTab } from './pm';
import { VisionApplication } from '../../application';
import { PMDTO, PMTypeDTO, PMCompleteRequestDTO } from './pmservice';
import { Measurement } from '../../components/measurement';
import { AssetDTO } from '../../service/asset';

/** This class represents the PM Complete Window, which is used to complete a PM instance.
 *  Set the next due values and includes a form for entering the completion date and
 *  the next due values, and a submit button to complete the PM.
 *  The class also includes logic to calculate the next due values based on the PM Type
 *  and the current asset telemetry.
 *
 */
export class PMCompleteWindow {
    window: dhtmlXWindowsCell;
    layout!: dhtmlXLayoutObject;
    form!: dhtmlXForm;

    tab: PreventativeMaintenanceTab;
    currentAsset: undefined | AssetDTO;
    currentPMType: undefined | PMTypeDTO;
    currentPM: undefined | PMDTO;
    submitBtn!: HTMLButtonElement;
    hoursOffset: number;
    odoOffsetKm: number;

    constructor(manager: VisionWindowManager, tab: PreventativeMaintenanceTab) {
        //const height = window.innerHeight * 0.75;
        //const width = window.innerWidth * 0.45;
        const height = 500;
        const width = 500;

        this.window = tab.layout.dhxWins.createWindow('complete_pm', 30, 30, width, height);
        this.tab = tab;

        this.window.attachEvent('onClose', (win) => {
            this.tab.completeWindow?.window.hide();
            return false;
        });

        this.window.setText('Complete PM');
        this.hoursOffset = 0;
        this.odoOffsetKm = 0;

        this.buildUI();
    }

    /** Load the PM data into the form and show the window.
     *
     * @param pmInstance The PM instance to load data for. If undefined, will just show the form with default values.
     * @param pmType The PM Type of the PM instance. Used to determine which due fields to show and how to calculate the next due values. If undefined, will not show any due fields or calculate next due values.
     * @param asset The asset of the PM instance. Used to calculate the next due values. If undefined, will not calculate next due values.
     * @returns void
     */
    public loadPM(pmInstance: PMDTO | undefined, pmType: PMTypeDTO | undefined, asset: AssetDTO | undefined): void {
        if (asset == undefined) {
            return;
        }
        this.currentPM = pmInstance;
        this.currentPMType = pmType;
        this.currentAsset = asset;

        this.form.hideItem('pm_due_odometer');
        this.form.hideItem('pm_current_odometer');
        this.form.hideItem('pm_completed_odometer');

        this.form.hideItem('pm_due_hours');
        this.form.hideItem('pm_current_hours');
        this.form.hideItem('pm_completed_hours');

        this.form.hideItem('pm_due_days');
        this.form.hideItem('pm_due_date');

        this.hoursOffset = asset?.hoursOffset ?? 0;
        this.odoOffsetKm = asset?.odoOffset ?? 0;

        VisionApplication.getInstance()
            .latestService.getLatestForAsset(asset?.id.id)
            .then((telemetry) => {
                if (pmType == undefined) {
                    return;
                }

                if (telemetry) {
                    const currentOdometerMiles = Measurement.km2miles(telemetry.odometer + this.odoOffsetKm);
                    const completedOdometerMiles = Measurement.km2miles(telemetry.odometer + this.odoOffsetKm);
                    const odoMiles = Measurement.km2miles(telemetry.odometer + this.odoOffsetKm + pmType.odometerIntervalKm);

                    this.form.setItemValue('pm_current_odometer', currentOdometerMiles.toString());
                    this.form.setItemValue('pm_completed_odometer', completedOdometerMiles.toString());
                    this.form.setItemValue('pm_due_odometer', odoMiles.toString());

                    const currentHours = telemetry.hours + this.hoursOffset;
                    const completedHours = telemetry.hours + this.hoursOffset;
                    const hours = telemetry.hours + this.hoursOffset + pmType.hoursInterval;
                    this.form.setItemValue('pm_current_hours', currentHours.toFixed(0).toString());
                    this.form.setItemValue('pm_completed_hours', completedHours.toFixed(0).toString());
                    this.form.setItemValue('pm_due_hours', hours.toFixed(0).toString());
                } else {
                    // No telemetry is available yet.   Need to test this some more.
                    const odoMiles = Measurement.km2miles(pmType.odometerIntervalKm + this.odoOffsetKm);
                    this.form.setItemValue('pm_due_odometer', odoMiles.toString());
                    this.form.setItemValue('pm_current_odometer', Measurement.km2miles(this.odoOffsetKm).toString());
                    this.form.setItemValue('pm_completed_odometer', Measurement.km2miles(this.odoOffsetKm).toString());

                    this.form.setItemValue('pm_due_hours', (pmType.hoursInterval + this.hoursOffset).toString());
                    this.form.setItemValue('pm_current_hours', this.hoursOffset.toString());
                    this.form.setItemValue('pm_completed_hours', this.hoursOffset.toString());
                }

                // Set the next due fields.
                if (pmType.odometerIntervalKm > 0) {
                    this.form.showItem('pm_due_odometer');
                    this.form.showItem('pm_current_odometer');
                    this.form.showItem('pm_completed_odometer');
                }

                if (pmType.hoursInterval > 0) {
                    this.form.showItem('pm_due_hours');
                    this.form.showItem('pm_current_hours');
                    this.form.showItem('pm_completed_hours');
                }

                if (pmType.daysInterval > 0) {
                    const dueDays = new Date().getTime() + pmType.daysInterval * 24 * 60 * 60 * 1000;
                    this.form.showItem('pm_due_days');

                    this.form.setItemValue('pm_due_days', new Date(dueDays));
                }
                if (pmType.dateInterval > 0) {
                    const dueDate = new Date(new Date(new Date().getFullYear(), pmType.dateMonth + pmType.dateInterval, pmType.dateDay).setHours(0, 0, 0, 0));

                    this.form.showItem('pm_due_date');
                    this.form.setItemValue('pm_due_date', new Date(dueDate));
                }
            });
    }

    /** Build the UI for the PM Complete Window. This includes the form fields and the submit button.
     *
     */
    private buildUI(): void {
        this.layout = this.window.attachLayout('1C');
        this.layout.cells('a').hideHeader();

        const formData = [
            {
                type: 'calendar',
                name: 'pm_complete_date',
                label: 'Completed On',
                enableTime: true,
                dateFormat: '%m-%d-%Y %H:%i',
                labelWidth: '220',
                position: 'label-left',
                offsetLeft: '12',
                autocomplete: 'off',
            },
            { type: 'input', name: 'pm_completed_odometer', label: 'Completed Odometer (miles)', labelWidth: '220', position: 'label-left', offsetLeft: '12', hidden: true },
            { type: 'input', name: 'pm_current_odometer', label: 'Current Odometer (miles)', labelWidth: '220', position: 'label-left', offsetLeft: '12', hidden: true },
            { type: 'input', name: 'pm_due_odometer', label: 'Next Due Odometer (miles)', labelWidth: '220', position: 'label-left', offsetLeft: '12', hidden: true },
            { type: 'input', name: 'pm_completed_hours', label: 'Completed Hours', labelWidth: '220', position: 'label-left', offsetLeft: '12', hidden: true },
            { type: 'input', name: 'pm_current_hours', label: 'Current Hours', labelWidth: '220', position: 'label-left', offsetLeft: '12', hidden: true },
            { type: 'input', name: 'pm_due_hours', label: 'Next Due Hours', labelWidth: '220', position: 'label-left', offsetLeft: '12', hidden: true },
            { type: 'calendar', name: 'pm_due_days', label: 'Next Due Date(days)', labelWidth: '220', position: 'label-left', offsetLeft: '12', hidden: true },
            { type: 'calendar', name: 'pm_due_date', label: 'Next Due Date(date)', labelWidth: '220', position: 'label-left', offsetLeft: '12', hidden: true },
        ];
        this.form = this.layout.cells('a').attachForm(formData);

        const btnWrapper = document.createElement('div');
        btnWrapper.className = 'custom-btn-wrapper';
        btnWrapper.id = 'submit_button';
        this.submitBtn = document.createElement('button');
        this.submitBtn.type = 'button';
        this.submitBtn.className = 'big-green-btn';
        this.submitBtn.textContent = 'Complete PM';
        this.submitBtn.disabled = false;
        this.submitBtn.addEventListener('click', () => {
            this.pmCompleted();
            this.window.hide();
        });

        this.form.attachEvent('onChange', (name: string, _value: string | number, _state: boolean) => {
            if (name == 'pm_completed_odometer') {
                this.recalculateDueOdometer();
            } else if (name == 'pm_completed_hours') {
                // Recalculate due hours if needed in the future
                this.recalculateDueHours();
            }
        });

        this.form.setItemValue('pm_complete_date', new Date());
        // Append button to wrapper, then wrapper to form container
        btnWrapper.appendChild(this.submitBtn);
        document.body.appendChild(btnWrapper);

        this.layout.attachFooter('submit_button');
    }

    /** Handle the PM completion logic when the submit button is clicked.
     * This includes gathering the data from the form, creating the PMCompleteRequestDTO,
     * and sending the completion request to the server.
     *
     */
    private pmCompleted(): void {
        console.log('PM has been completed');

        const dto = {} as PMCompleteRequestDTO;
        if (this.currentPM) {
            dto.pmId = this.currentPM.id;
            dto.completedDate = this.form.getItemValue('pm_complete_date').getTime();

            if (this.form.getItemValue('pm_due_hours') != '') {
                dto.dueAtHours = this.form.getItemValue('pm_due_hours');
            }

            if (this.form.getItemValue('pm_due_odometer') != '') {
                dto.dueAtKm = Measurement.miles2km(this.form.getItemValue('pm_due_odometer'));
            }

            const formPmDueDays = this.form.getItemValue('pm_due_days');
            if (formPmDueDays == '' || formPmDueDays == null) {
                dto.dueAtDays = 0;
            } else {
                dto.dueAtDays = formPmDueDays.getTime();
            }

            const formPmDueDate = this.form.getItemValue('pm_due_date');
            if (formPmDueDate == '' || formPmDueDate == null) {
                dto.dueAtDate = 0;
            } else {
                dto.dueAtDate = formPmDueDate.getTime();
            }

            dto.currentOdometerKm = Measurement.miles2km(this.form.getItemValue('pm_current_odometer'));
            dto.currentHours = this.form.getItemValue('pm_current_hours');

            dto.completedOdometerKm = Measurement.miles2km(this.form.getItemValue('pm_completed_odometer'));
            dto.completedHours = this.form.getItemValue('pm_completed_hours');

            // Raw value for user entered value.
            dto.vehicleOdoDueAtKm = Measurement.miles2km(this.form.getItemValue('pm_due_odometer'));
        }

        this.tab.service.completedRequestPM(dto.pmId, dto).then((_response) => {
            dhtmlx.message({
                title: 'Completed',
                text: 'PM was completed',
                type: 'alert-success',
            });

            // Make the asset list dirty and reload the data.
            if (this.currentAsset) {
                this.tab.service.dirty = true;
                VisionApplication.getInstance().assetService.dirty = true;
                VisionApplication.getInstance().assetService.setAssetMapDirty();
            }
            this.tab.loadData();
            window.close();
        });
    }

    /** This isn't used anymore?
     *
     */
    private getInfoAtDateTime(): void {
        const datetime = this.form.getItemValue('pm_complete_date').getTime();
        const assetId = this.currentPM?.assetId ?? this.currentAsset?.id.id;
        if (assetId != null) {
            this.tab.service.getInfoAt(datetime, assetId).then((data) => {
                if (data.parsedBody) {
                    const hours = data.parsedBody?.hours;
                    const odokm = data.parsedBody?.odometerKm;
                    this.setCurrentValues(odokm, hours);
                }
            });
        }

        alert('Remove getInfoDatatetime or implement if needed');
    }

    /** Recalculate the next due odometer value based on the completed odometer value and the PM Type's odometer interval.
     *
     */
    private recalculateDueOdometer() {
        let lastDoneOdometerKm = Measurement.miles2km(parseFloat(this.form.getItemValue('pm_completed_odometer')));

        lastDoneOdometerKm -= this.odoOffsetKm;
        if (this.currentPMType) {
            const dueOdometerKm = lastDoneOdometerKm + this.currentPMType.odometerIntervalKm;
            const dueOdometerMiles = Measurement.km2miles(dueOdometerKm + this.odoOffsetKm);
            this.form.setItemValue('pm_due_odometer', dueOdometerMiles.toString());
        }
    }

    private recalculateDueHours() {
        let lastDoneHours = parseFloat(this.form.getItemValue('pm_completed_hours'));
        lastDoneHours -= this.hoursOffset;
        if (this.currentPMType) {
            const dueHours = lastDoneHours + this.currentPMType.hoursInterval;
            this.form.setItemValue('pm_due_hours', dueHours.toString());
        }
    }

    // Not used anymore?
    private setCurrentValues(odokm: number, hours: number): void {
        if (this.currentAsset && this.currentPMType) {
            const displayOdometer = Measurement.km2miles(odokm + this.currentAsset.odoOffset);
            this.form.setItemValue('pm_complete_odometer', displayOdometer);
            this.form.setItemValue('pm_complete_hours', hours);

            if (this.currentPMType.type == PM_Type.HOURS) {
                this.form.setItemValue('pm_next_hours', hours + this.currentAsset.hoursOffset + this.currentPMType.interval);
            } else if (this.currentPMType.type == PM_Type.MILES) {
                const nextOdometer = Measurement.km2miles(odokm + this.currentAsset.odoOffset + this.currentPMType.interval);
                this.form.setItemValue('pm_next_odometer', nextOdometer);
            }
        }
    }
}
